"use client";

import Link from "@/components/locale-link";
import {
  CopyButton,
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { AbstractScreeningToolStrings } from "@/components/tools/abstract-screening-tool";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type RefRecord,
  recordsToRis,
} from "@/lib/tool-kit/research-discovery/records";
import {
  exportCsv,
  recordsWith,
  type ScreeningSummary,
  type Verdicts,
} from "@/lib/tool-kit/research-discovery/screening";

const RIS_MIME = "application/x-research-info-systems;charset=utf-8";
const CSV_MIME = "text/csv;charset=utf-8";

function prismaText(
  summary: ScreeningSummary,
  strings: AbstractScreeningToolStrings,
  fmt: (value: number) => string
): string {
  const lines = [
    t(strings.prismaLine, {
      screened: fmt(summary.screened),
      excluded: fmt(summary.exclude),
      sought: fmt(summary.include + summary.maybe),
      included: fmt(summary.include),
      maybe: fmt(summary.maybe),
    }),
  ];
  for (const { reason, count } of summary.reasons) {
    lines.push(`${reason}: ${fmt(count)}`);
  }
  return lines.join("\n");
}

export function ScreeningSummaryPanel({
  records,
  verdicts,
  summary,
  strings,
}: {
  records: readonly RefRecord[];
  verdicts: Verdicts;
  summary: ScreeningSummary;
  strings: AbstractScreeningToolStrings;
}) {
  const fmt = useToolNumber();
  const included = recordsWith(records, verdicts, ["include"]);
  const soughtList = recordsWith(records, verdicts, ["include", "maybe"]);

  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-5`}>
      <h2 className={WIDGET_H2}>{strings.summaryHeading}</h2>
      <StatGrid>
        <StatCard label={strings.statTotal} value={fmt(summary.total)} />
        <StatCard
          emphasis
          label={strings.statScreened}
          value={fmt(summary.screened)}
        />
        <StatCard
          label={strings.statUndecided}
          value={fmt(summary.undecided)}
        />
        <StatCard
          label={strings.decisions.include}
          value={fmt(summary.include)}
        />
        <StatCard label={strings.decisions.maybe} value={fmt(summary.maybe)} />
        <StatCard
          label={strings.decisions.exclude}
          value={fmt(summary.exclude)}
        />
      </StatGrid>
      {summary.reasons.length > 0 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.reasonsHeading}</h3>
          <DataTable
            head={[strings.colReason, strings.colCount]}
            rows={summary.reasons.map((row) => [row.reason, fmt(row.count)])}
          />
        </div>
      ) : null}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.prismaHeading}</h3>
        <p className="rounded-lg border border-border bg-card p-3 text-foreground text-sm">
          {prismaText(summary, strings, fmt).split("\n")[0]}
        </p>
        <p className={HINT}>
          {strings.prismaNote}{" "}
          <Link
            className="text-primary underline-offset-4 hover:underline"
            href="/tools/prisma-flow-diagram-generator"
          >
            {strings.flowLink}
          </Link>
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_PRIMARY}
          onClick={() =>
            downloadText(
              `${strings.fileName}.csv`,
              exportCsv(records, verdicts),
              CSV_MIME
            )
          }
          type="button"
        >
          {strings.downloadCsv}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={included.length === 0}
          onClick={() =>
            downloadText(
              `${strings.fileName}-included.ris`,
              recordsToRis(included),
              RIS_MIME
            )
          }
          type="button"
        >
          {t(strings.downloadIncluded, { count: fmt(included.length) })}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={soughtList.length === 0}
          onClick={() =>
            downloadText(
              `${strings.fileName}-included-maybe.ris`,
              recordsToRis(soughtList),
              RIS_MIME
            )
          }
          type="button"
        >
          {t(strings.downloadSought, { count: fmt(soughtList.length) })}
        </button>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyCounts}
          text={prismaText(summary, strings, fmt)}
        />
      </div>
      <p className={HINT}>{strings.saveNote}</p>
    </section>
  );
}
